import { differenceInDays } from "date-fns";
import { EXTRA_TYPE, DATE_SELECTION_TYPE } from "../../../../witadmin/Common/constants";

export type ExtraType = keyof typeof EXTRA_TYPE;
export type DateSelectionType = keyof typeof DATE_SELECTION_TYPE;

export interface Extra {
  ticker: string;
  name: string;
  quantity: number;
  amount: { amount: number; currency: string };
  obligatory?: boolean;
  percentage?: boolean;
  dateQuantity?: Record<string, number>;
  type: ExtraType;
  dateSelectionType: DateSelectionType;
  appliesToGuests?: string[];
  totalRatePerDay?: Record<string, number>;
}

export interface ExtraWithFlags extends Extra {
  isAppliedToCurrentInventoryTicker: boolean;
  isAvailable: boolean;
  isRestricted: boolean;
}

export interface InventoryConfiguration {
  configuration: {
    adult?: number;
    baby?: number;
    child?: number;
    senior?: number;
    teenager?: number;
  };
}

export class ExtraSelectionEntity {
  private stayDates: string[];
  private catalogExtras: ExtraWithFlags[];
  private currentInventory?: InventoryConfiguration;

  constructor(
    checkIn: string,
    checkOut: string,
    catalogExtras: ExtraWithFlags[],
    currentInventory?: InventoryConfiguration
  ) {
    this.stayDates = this.calculateStayDates(checkIn, checkOut);
    this.catalogExtras = catalogExtras;
    this.currentInventory = currentInventory;
  }

  private calculateStayDates(checkIn: string, checkOut: string): string[] {
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    const days = differenceInDays(end, start);
    return Array.from({ length: days }, (_, index) => {
      const date = new Date(start);
      date.setDate(start.getDate() + index);
      return this.formatDate(date);
    });
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  getStayDates(): string[] {
    return this.stayDates;
  }

  stripFlags(extra: ExtraWithFlags): Extra {
    const { isAppliedToCurrentInventoryTicker, isAvailable, isRestricted, ...rest } = extra;
    return rest;
  }

  hasMatchingOccupancy(extra: ExtraWithFlags): boolean {
    if (extra.type !== EXTRA_TYPE.PERSON) {
      return true;
    }

    if (!extra.appliesToGuests || extra.appliesToGuests.length === 0 || !this.currentInventory) {
      return false;
    }

    const config = this.currentInventory.configuration;

    return extra.appliesToGuests.some(
      guestType => (config[guestType.toLowerCase() as keyof typeof config] || 0) > 0
    );
  }

  calculatePersonQuantity(appliesToGuests: string[] | undefined): number {
    if (!appliesToGuests || appliesToGuests.length === 0) {
      throw new Error("appliesToGuests is required for person quantity calculation");
    }

    if (!this.currentInventory) {
      throw new Error("Inventory configuration is required for person quantity calculation");
    }

    const config = this.currentInventory.configuration;
    const totalOccupancy = appliesToGuests.reduce((sum, guestType) => {
      const key = guestType.toLowerCase() as keyof typeof config;
      return sum + (config[key] || 0);
    }, 0);

    if (totalOccupancy === 0) {
      throw new Error("Total occupancy cannot be zero for person quantity calculation");
    }

    return totalOccupancy;
  }

  getCatalogPrice(ticker: string): number {
    const catalogExtra = this.catalogExtras.find(extra => extra.ticker === ticker);
    if (!catalogExtra) {
      throw new Error(`Extra with ticker "${ticker}" not found in catalog`);
    }
    return catalogExtra.amount.amount;
  }

  calculatePerUnitPrice(current: ExtraWithFlags): number {
    return current.amount.amount / current.quantity;
  }

  getActualDateValue(extra: ExtraWithFlags, value: number): number {
    const shouldCalculatePersonCount =
      extra.type === EXTRA_TYPE.PERSON &&
      extra.dateSelectionType === DATE_SELECTION_TYPE.SPECIFIC &&
      value === 1;

    if (shouldCalculatePersonCount) {
      return this.calculatePersonQuantity(extra.appliesToGuests);
    }

    return value;
  }

  private getDefaultQuantity(extra: ExtraWithFlags): number {
    const isSpecific = extra.dateSelectionType === DATE_SELECTION_TYPE.SPECIFIC;
    const isAll = extra.dateSelectionType === DATE_SELECTION_TYPE.ALL;

    if (extra.type === EXTRA_TYPE.PERSON && (isSpecific || isAll)) {
      return this.calculatePersonQuantity(extra.appliesToGuests);
    }
    if (extra.type === EXTRA_TYPE.ROOM && (isSpecific || isAll)) {
      return 1;
    }
    if (extra.type === EXTRA_TYPE.UNIT && isSpecific) {
      return 1;
    }
    return 1;
  }

  initializeExtra(extra: ExtraWithFlags, date?: string, value?: number): ExtraWithFlags {
    const catalogPrice = this.getCatalogPrice(extra.ticker);

    if (extra.dateSelectionType === DATE_SELECTION_TYPE.NONE) {
      const totalRatePerDay = { [this.stayDates[0]]: catalogPrice };
      if (extra.type === EXTRA_TYPE.PERSON) {
        const personCount = this.calculatePersonQuantity(extra.appliesToGuests);
        return {
          ...extra,
          quantity: personCount,
          amount: { ...extra.amount, amount: personCount * catalogPrice },
          totalRatePerDay,
          dateQuantity: { [this.stayDates[0]]: personCount },
        };
      }

      if (
        extra.type === EXTRA_TYPE.ROOM ||
        (extra.type === EXTRA_TYPE.UNIT && extra.dateSelectionType === DATE_SELECTION_TYPE.NONE)
      ) {
        const quantity = 1;
        return {
          ...extra,
          quantity,
          amount: { ...extra.amount, amount: catalogPrice },
          totalRatePerDay,
          dateQuantity: { [this.stayDates[0]]: quantity },
        };
      }

      return { ...extra, totalRatePerDay };
    }

    const defaultValue = this.getDefaultQuantity(extra);
    const initialDateQuantity =
      date && value
        ? { [date]: value }
        : Object.fromEntries(this.stayDates.map(date => [date, defaultValue]));
    const totalQuantity = Object.values(initialDateQuantity).reduce((sum, qty) => sum + qty, 0);
    const totalAmount = totalQuantity * catalogPrice;
    const totalRatePerDay = Object.fromEntries(this.stayDates.map(date => [date, catalogPrice]));

    return {
      ...extra,
      dateQuantity: initialDateQuantity,
      quantity: totalQuantity,
      amount: { ...extra.amount, amount: totalAmount },
      totalRatePerDay,
    };
  }

  updateUnitQuantity(
    quantity: number,
    currentExtra: ExtraWithFlags | undefined
  ): ExtraWithFlags | null {
    if (quantity === 0) {
      return null;
    }

    if (!currentExtra) {
      throw new Error("currentExtra is required for updateUnitQuantity");
    }

    const perUnitPrice = this.calculatePerUnitPrice(currentExtra);
    const totalAmount = quantity * perUnitPrice;
    const totalRatePerDay =
      currentExtra.totalRatePerDay ||
      Object.fromEntries(this.stayDates.map(date => [date, perUnitPrice]));

    const result: ExtraWithFlags = {
      ...currentExtra,
      quantity,
      amount: { ...currentExtra.amount, amount: totalAmount },
      totalRatePerDay,
    };

    const isUnitOrRoom =
      currentExtra.type === EXTRA_TYPE.UNIT || currentExtra.type === EXTRA_TYPE.ROOM;

    if (isUnitOrRoom && currentExtra.dateSelectionType === DATE_SELECTION_TYPE.ALL) {
      const unitsPerDay = quantity / this.stayDates.length;
      result.dateQuantity = Object.fromEntries(this.stayDates.map(date => [date, unitsPerDay]));
    }

    if (
      currentExtra.type === EXTRA_TYPE.UNIT &&
      currentExtra.dateSelectionType === DATE_SELECTION_TYPE.NONE
    ) {
      result.dateQuantity = { [this.stayDates[0]]: quantity };
    }

    return result;
  }

  updateDateValue(
    date: string,
    value: number,
    currentExtra: ExtraWithFlags | undefined
  ): ExtraWithFlags | null {
    if (!currentExtra) {
      throw new Error("currentExtra is required for updateDateValue");
    }

    const actualValue = this.getActualDateValue(currentExtra, value);
    const perUnitPrice = this.calculatePerUnitPrice(currentExtra);

    const newDateQuantity = { ...(currentExtra.dateQuantity || {}), [date]: actualValue };
    const totalQuantity = Object.values(newDateQuantity).reduce((sum, qty) => sum + qty, 0);

    if (totalQuantity === 0) {
      return null;
    }

    const totalAmount = totalQuantity * perUnitPrice;

    return {
      ...currentExtra,
      dateQuantity: newDateQuantity,
      quantity: totalQuantity,
      amount: { ...currentExtra.amount, amount: totalAmount },
      totalRatePerDay:
        currentExtra.totalRatePerDay ||
        Object.fromEntries(this.stayDates.map(day => [day, perUnitPrice])),
    };
  }

  getDisplayPrice(extra: ExtraWithFlags, selectedExtra: ExtraWithFlags | undefined): number {
    if (!selectedExtra) {
      return this.getCatalogPrice(extra.ticker);
    }

    // Special case: UNIT with NONE date selection - show first totalRatePerDay
    if (
      extra.type === EXTRA_TYPE.UNIT &&
      extra.dateSelectionType === DATE_SELECTION_TYPE.NONE &&
      selectedExtra.totalRatePerDay
    ) {
      return Object.values(selectedExtra.totalRatePerDay)[0];
    }

    // All other selected extras: show per-unit rate
    return selectedExtra.quantity > 0 ? selectedExtra.amount.amount / selectedExtra.quantity : 0;
  }

  getSelectValue(extra: ExtraWithFlags, selectedExtra: ExtraWithFlags | undefined): number {
    if (!selectedExtra) {
      return 0;
    }

    if (extra.dateSelectionType === DATE_SELECTION_TYPE.ALL) {
      return (selectedExtra.quantity ?? 0) / this.stayDates.length || 0;
    }

    return selectedExtra.quantity ?? 0;
  }

  calculateFinalQuantity(extra: ExtraWithFlags, selectedUnits: number): number {
    if (extra.dateSelectionType === DATE_SELECTION_TYPE.ALL) {
      return selectedUnits * this.stayDates.length;
    }

    return selectedUnits;
  }

  mergeExtras(selectedExtras: ExtraWithFlags[]): ExtraWithFlags[] {
    const selectedMap = new Map(selectedExtras.map(extra => [extra.ticker, extra]));

    return this.catalogExtras
      .filter(catalogExtra => this.hasMatchingOccupancy(catalogExtra))
      .map(catalogExtra => {
        const selectedVersion = selectedMap.get(catalogExtra.ticker);
        return selectedVersion || catalogExtra;
      });
  }
}
